/**
 * PlyxInstallPrompt — Plyx-branded PWA "Add to Home Screen" prompt.
 *
 * Shows a floating pill on Plyx routes when:
 *   • The browser fires `beforeinstallprompt` (Android / Desktop Chrome / Edge), OR
 *   • The device is iOS Safari (no native prompt — we surface a custom
 *     instructions modal instead).
 *
 * The user can dismiss the pill — we snooze it for 14 days via localStorage.
 * Once the app is actually installed, the pill stays hidden permanently.
 *
 * This component is intentionally self-contained: it does not require the
 * parent to wire any state, just drop it inside the Plyx layout once.
 */
import React, { useEffect, useState } from "react";
import { Download, X, Share2, Plus } from "lucide-react";
import useInstallPrompt from "@/hooks/useInstallPrompt";
import { toast } from "sonner";
import { trackPlyxInstall } from "@/lib/trackPlyxInstall";

const STORAGE_KEY = "plyx-install-dismissed-at";
// Per-SESSION snooze only — once the user closes the pill, we hide it for
// the rest of this browser tab/session, but the very next time they open
// Plyx the pill returns. This keeps the install affordance "easily
// available anytime" rather than locking it behind a 14-day cooldown.
const SESSION_DISMISSED_KEY = "plyx-install-dismissed-session";

const PlyxInstallPrompt = () => {
  const { canInstall, installed, isIos, promptInstall } = useInstallPrompt();
  const [visible, setVisible] = useState(false);
  const [iosOpen, setIosOpen] = useState(false);

  useEffect(() => {
    if (installed || !canInstall) { setVisible(false); return; }
    // Only suppress within the same browser session — reopens on next visit.
    if (sessionStorage.getItem(SESSION_DISMISSED_KEY)) { setVisible(false); return; }
    // Small delay so the pill doesn't fight first paint on the feed.
    const t = setTimeout(() => {
      setVisible(true);
      trackPlyxInstall("pill_shown", { source: "auto", ios: isIos });
    }, isIos ? 3500 : 1500);
    return () => clearTimeout(t);
  }, [canInstall, installed, isIos]);

  const dismiss = () => {
    sessionStorage.setItem(SESSION_DISMISSED_KEY, "1");
    setVisible(false);
    setIosOpen(false);
    trackPlyxInstall("dismissed");
  };

  const onInstalledSnooze = () => {
    // Permanent suppression once they've actually installed (the
    // useInstallPrompt hook detects standalone display mode anyway, but
    // this is belt-and-braces in case the OS reports late).
    localStorage.setItem(STORAGE_KEY, String(Date.now()));
    setVisible(false);
    setIosOpen(false);
  };

  const install = async () => {
    trackPlyxInstall("started", { from: "pill" });
    const result = await promptInstall();
    if (result === "installed") {
      trackPlyxInstall("completed", { from: "pill" });
      // Reward the install with the brand chime + visual burst.
      try {
        const { playPlyxChime } = await import("@/lib/plyxChime");
        playPlyxChime({ volume: 0.7, force: true });
        const { celebratePlyx } = await import("@/components/plyx/PlyxCelebration");
        celebratePlyx({ message: "Welcome to Plyx ✨" });
      } catch { /* ignore */ }
      onInstalledSnooze();
    } else if (result === "ios") {
      trackPlyxInstall("ios_modal_opened");
      setIosOpen(true);
    } else if (result === "dismissed") {
      trackPlyxInstall("native_dismissed");
    }
  };

  // Always-available manual trigger via the global `plyx-show-install`
  // event — lets the bottom-nav (or any other surface) re-open the prompt
  // even after the auto-snooze. Wired up via window.dispatchEvent.
  useEffect(() => {
    const handler = () => {
      if (installed) return;
      if (canInstall) setVisible(true);
      else if (isIos) setIosOpen(true);
    };
    window.addEventListener("plyx-show-install", handler);
    return () => window.removeEventListener("plyx-show-install", handler);
  }, [canInstall, installed, isIos]);

  if (!visible && !iosOpen) return null;

  return (
    <>
      {visible && (
        <div
          data-testid="plyx-install-prompt"
          className="fixed left-3 right-3 bottom-20 sm:bottom-4 z-[60] sm:left-1/2 sm:-translate-x-1/2 sm:right-auto sm:w-[420px] rounded-2xl shadow-2xl border border-white/10 overflow-hidden"
          style={{
            background: "linear-gradient(135deg, rgba(255,45,122,0.95) 0%, rgba(255,180,45,0.95) 50%, rgba(0,224,199,0.95) 100%)",
            backdropFilter: "blur(8px)",
          }}
        >
          <div className="flex items-center gap-3 p-3 pr-2 bg-black/35">
            <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center shrink-0 border border-white/20">
              <span className="text-white font-display text-lg leading-none">▶</span>
            </div>
            <div className="flex-1 min-w-0 text-white">
              <div className="font-semibold text-sm">Install Plyx on your phone</div>
              <div className="text-xs text-white/85">One-tap access, no app store needed.</div>
            </div>
            <button
              type="button"
              data-testid="plyx-install-prompt-install"
              onClick={install}
              className="bg-white text-black font-semibold rounded-full h-9 px-4 text-xs inline-flex items-center gap-1.5 hover:bg-white/90 transition"
            >
              <Download className="w-3.5 h-3.5" /> Install
            </button>
            <button
              type="button"
              data-testid="plyx-install-prompt-dismiss"
              onClick={dismiss}
              className="text-white/75 hover:text-white p-2 rounded-md"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {iosOpen && (
        <div
          data-testid="plyx-install-ios-modal"
          className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-4 bg-black/65"
          onClick={() => setIosOpen(false)}
        >
          <div
            className="bg-[#0A0A0A] text-white rounded-2xl max-w-md w-full p-6 text-center border border-white/10"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="w-14 h-14 mx-auto rounded-2xl flex items-center justify-center mb-3"
              style={{ background: "#E85D4E" }}
            >
              <span className="font-display text-2xl text-black leading-none">▶</span>
            </div>
            <div className="font-display text-2xl mb-1">Add Plyx to your Home Screen</div>
            <p className="text-sm text-white/70 mb-5">Two taps in Safari and you&apos;re done.</p>
            <ol className="text-left text-sm space-y-3 mb-5">
              <li className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-gradient-to-r from-[#FF2D7A] via-[#FFB42D] to-[#00E0C7] text-white text-xs font-bold flex items-center justify-center shrink-0">1</span>
                <span>
                  Tap the <Share2 className="inline w-4 h-4 -mt-1 mx-1" /> <b>Share</b> button at the bottom of Safari.
                </span>
              </li>
              <li className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-[#FFB42D] text-black text-xs font-bold flex items-center justify-center shrink-0">2</span>
                <span>
                  Scroll and pick <Plus className="inline w-4 h-4 -mt-1 mx-1" /> <b>Add to Home Screen</b>.
                </span>
              </li>
            </ol>
            <button
              type="button"
              onClick={dismiss}
              data-testid="plyx-install-ios-done"
              className="w-full rounded-full h-11 font-semibold text-black"
              style={{ background: "#E85D4E" }}
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default PlyxInstallPrompt;
